import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, Zap, Gem, Heart } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { WhatsAppIcon, whatsappUrl } from "@/components/WhatsAppButton";
import { toast } from "sonner";
import DOMPurify from "dompurify";

const ProductDetail = () => {
  const { id } = useParams();
  const { addItem, openCart } = useCart();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="flex min-h-[70vh] flex-col items-center justify-center bg-cream px-4 text-center">
        <p data-testid="product-not-found" className="font-serif text-3xl text-espresso">
          This piece has slipped away
        </p>
        <Link
          to="/shop"
          className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-muted underline-offset-4 hover:underline"
        >
          Back to the collection
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-screen bg-cream px-4 py-12 sm:px-8 lg:px-16">
        <div className="mx-auto grid max-w-6xl animate-pulse grid-cols-1 gap-12 md:grid-cols-2">
          <div className="aspect-square bg-cream-dark" />
          <div className="space-y-4 pt-8">
            <div className="h-4 w-32 bg-cream-dark" />
            <div className="h-8 w-3/4 bg-cream-dark" />
            <div className="h-6 w-24 bg-cream-dark" />
          </div>
        </div>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [];

  const specs = [
    ["Material", product.material],
    ["Gemstone", product.gemstone],
    ["Size", product.size],
    ["SKU", product.sku],
    ["Care", product.care_instructions],
  ].filter(([, v]) => v);

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to your cart`, {
      action: { label: "View", onClick: openCart },
    });
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    openCart();
  };

  return (
    <main data-testid="product-detail-container" className="min-h-screen bg-cream px-4 py-10 sm:px-8 lg:px-16 lg:py-16">
      <div className="mx-auto max-w-6xl">
        <Link
          to="/shop"
          data-testid="product-detail-back-link"
          className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-espresso/60 transition-colors hover:text-gold-accent"
        >
          <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to collection
        </Link>

        <div className="mt-8 grid grid-cols-1 gap-12 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative overflow-hidden rounded-sm border border-gold/20 bg-cream-dark">
              {images.length ? (
                <img
                  src={images[activeImg]}
                  alt={product.name}
                  data-testid="product-detail-main-image"
                  className="aspect-square w-full object-cover"
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center">
                  <Gem size={40} strokeWidth={1} className="text-gold-muted/40" />
                </div>
              )}
              {product.new_arrival && (
                <span className="absolute left-4 top-4 bg-espresso px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
                  New Arrival
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-detail-thumbnail-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`h-20 w-16 shrink-0 overflow-hidden rounded-sm border transition-all duration-300 ${
                      i === activeImg ? "border-gold" : "border-espresso/10 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold-muted">
                {product.category}
              </span>
              {product.gemstone && (
                <span className="bg-cream-dark px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-espresso/70">
                  {product.gemstone}
                </span>
              )}
            </div>

            <h1 className="mt-5 font-serif text-4xl leading-tight tracking-tight text-espresso sm:text-5xl">
              {product.name}
            </h1>

            <div className="mt-5 flex items-baseline gap-4">
              <p data-testid="product-detail-price" className="price-text text-4xl text-espresso">
                {formatINR(product.price)}
              </p>
              {product.original_price > product.price && (
                <>
                  <p className="price-text text-lg font-normal text-espresso/50 line-through">{formatINR(product.original_price)}</p>
                  <span className="bg-gold/15 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gold-muted">
                    Save {Math.round((1 - product.price / product.original_price) * 100)}%
                  </span>
                </>
              )}
            </div>

            <p
              data-testid="product-detail-stock-status"
              className={`mt-3 font-mono text-[10px] uppercase tracking-[0.25em] ${
                product.in_stock ? "text-gold-muted" : "text-red-700"
              }`}
            >
              {product.in_stock ? "In stock — ready to ship" : "Currently out of stock"}
            </p>

            {product.description && (
              <p
                data-testid="product-detail-description"
                className="mt-6 text-justify text-base leading-relaxed text-espresso/70"
              >
                {product.description}
              </p>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-espresso/15">
                <button
                  data-testid="product-detail-qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="flex h-12 w-11 items-center justify-center text-espresso transition-colors hover:text-gold-accent"
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span data-testid="product-detail-qty" className="w-10 text-center font-mono text-sm">
                  {qty}
                </span>
                <button
                  data-testid="product-detail-qty-increase"
                  onClick={() => setQty((q) => q + 1)}
                  className="flex h-12 w-11 items-center justify-center text-espresso transition-colors hover:text-gold-accent"
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                data-testid="product-detail-add-to-cart-button"
                onClick={handleAdd}
                disabled={!product.in_stock}
                className="inline-flex flex-1 items-center justify-center gap-3 border-2 border-espresso px-8 py-4 font-sans text-xs font-bold uppercase tracking-[0.2em] text-espresso transition-all duration-300 hover:border-gold hover:bg-gold/10 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
              >
                <ShoppingBag size={16} /> Add to Cart
              </button>
              <button
                data-testid="product-detail-buy-now-button"
                onClick={handleBuyNow}
                disabled={!product.in_stock}
                className="inline-flex flex-1 items-center justify-center gap-3 bg-gold px-8 py-4 font-sans text-xs font-bold uppercase tracking-[0.2em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
              >
                <Zap size={15} /> Buy Now
              </button>
              <button
                data-testid="product-detail-wishlist-button"
                onClick={() => {
                  const saved = inWishlist(product.id);
                  toggleWishlist(product);
                  toast.success(saved ? "Removed from your wishlist" : "Saved to your wishlist");
                }}
                className="inline-flex h-[52px] w-[52px] items-center justify-center border border-espresso/15 text-espresso transition-all duration-300 hover:border-gold active:scale-95"
                aria-label="Toggle wishlist"
              >
                <Heart
                  size={17}
                  className={inWishlist(product.id) ? "fill-gold-accent text-gold-accent" : ""}
                />
              </button>
            </div>

            <a
              href={whatsappUrl(
                `Hi, I have a question about the ${product.name} (${formatINR(product.price)}).`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-ask-whatsapp"
              className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#1da851] transition-colors duration-300 hover:text-[#25D366]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Ask about this piece
            </a>

            {specs.length > 0 && (
              <dl className="mt-10 divide-y divide-espresso/8 border-t border-espresso/8">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex gap-6 py-3.5">
                    <dt className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-muted">
                      {k}
                    </dt>
                    <dd className="text-sm leading-relaxed text-espresso/75">{v}</dd>
                  </div>
                ))}
              </dl>
            )}

            {product.about_html && (
              <section data-testid="product-about-section" className="mt-10 border-t border-espresso/10 pt-8">
                <h2 className="font-serif text-2xl tracking-tight text-espresso">About this product</h2>
                <div
                  data-testid="product-about-content"
                  className="about-content mt-4 text-sm text-espresso/75"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.about_html) }}
                />
              </section>
            )}
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
